/**
 * "Security in plain English" — Trust section (the "Is Syncanix safe?" page).
 *
 * A single narrative a non-technical, security-conscious buyer can read top to
 * bottom and come away reassured: the one core idea, where data lives, how
 * customers are kept apart, who can see data, what the AI model sees, what the
 * customer controls, and an honest box about what is and isn't certified yet.
 *
 * Synthesised from the existing trust + core-concepts prose — it must stay
 * inside the real posture (`.claude/rules/security.md`,
 * tenant-isolation-invariant.md): four-layer isolation + nightly cross-tenant
 * probes, EU residency pinned at build time, ZDR via Bedrock-EU, least-privilege
 * access. Certifications are stated as in-progress; the injection posture is
 * "mitigated, not eliminated". No claim here exceeds those pages.
 */
import type { DocPageContent } from '../types';

export const TRUST_SECURITY_PAGE: DocPageContent = {
  slug: 'trust/security',
  titleKey: 'docs.trust.security.title',
  descriptionKey: 'docs.trust.security.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.trust.security.lead' },
    {
      kind: 'callout',
      tone: 'important',
      titleKey: 'docs.trust.security.idea.title',
      textKey: 'docs.trust.security.idea.body',
    },

    {
      kind: 'heading',
      level: 2,
      id: 'residency',
      textKey: 'docs.trust.security.residency.heading',
    },
    {
      kind: 'paragraph',
      textKey: 'docs.trust.security.residency.body',
      links: {
        residency: {
          target: { docSlug: 'trust/residency' },
          labelKey: 'docs.trust.security.residency.link',
        },
      },
    },

    {
      kind: 'heading',
      level: 2,
      id: 'isolation',
      textKey: 'docs.trust.security.isolation.heading',
    },
    { kind: 'paragraph', textKey: 'docs.trust.security.isolation.body' },
    {
      kind: 'list',
      itemKeys: [
        'docs.trust.security.isolation.item.boundary',
        'docs.trust.security.isolation.item.query',
        'docs.trust.security.isolation.item.database',
        'docs.trust.security.isolation.item.probes',
      ],
    },

    { kind: 'heading', level: 2, id: 'access', textKey: 'docs.trust.security.access.heading' },
    { kind: 'paragraph', textKey: 'docs.trust.security.access.body' },

    { kind: 'heading', level: 2, id: 'model', textKey: 'docs.trust.security.model.heading' },
    { kind: 'paragraph', textKey: 'docs.trust.security.model.body' },
    {
      kind: 'list',
      itemKeys: [
        'docs.trust.security.model.item.sees',
        'docs.trust.security.model.item.never',
        'docs.trust.security.model.item.retain',
      ],
    },

    { kind: 'heading', level: 2, id: 'control', textKey: 'docs.trust.security.control.heading' },
    { kind: 'paragraph', textKey: 'docs.trust.security.control.body' },
    {
      kind: 'list',
      itemKeys: [
        'docs.trust.security.control.item.actions',
        'docs.trust.security.control.item.retention',
        'docs.trust.security.control.item.memory',
        'docs.trust.security.control.item.byok',
        'docs.trust.security.control.item.export',
      ],
    },

    {
      kind: 'heading',
      level: 2,
      id: 'anonymous',
      textKey: 'docs.trust.security.anonymous.heading',
    },
    { kind: 'paragraph', textKey: 'docs.trust.security.anonymous.body' },
    { kind: 'paragraph', textKey: 'docs.trust.security.anonymous.posture' },

    {
      kind: 'callout',
      tone: 'note',
      titleKey: 'docs.trust.security.honest.title',
      textKey: 'docs.trust.security.honest.body',
    },

    { kind: 'heading', level: 2, id: 'next', textKey: 'docs.common.nextSteps' },
    {
      kind: 'cardGrid',
      cards: [
        {
          titleKey: 'docs.trust.security.next.boundaries.title',
          textKey: 'docs.trust.security.next.boundaries.text',
          to: { docSlug: 'boundaries' },
        },
        {
          titleKey: 'docs.trust.security.next.data.title',
          textKey: 'docs.trust.security.next.data.text',
          to: { docSlug: 'trust/data' },
        },
        {
          titleKey: 'docs.trust.security.next.compliance.title',
          textKey: 'docs.trust.security.next.compliance.text',
          to: { docSlug: 'trust/compliance' },
        },
      ],
    },
  ],
};
